/**
 * scrollToChatMessage — scroll the thread to one message and mark it with a soft,
 * borderless cue (Step 11c, SPEC section 8 A and C): the message is brought to the
 * middle of the thread, washed with a faint accent tint that fades out, and settles
 * with a slight lift. Reduced motion keeps only the tint. The thread renders each message under
 * `data-testid="message-${id}"`. When the message is not in the DOM yet, the
 * caller's backfill runs once and the lookup retries for a few frames.
 */

const CUE_MS = 1400
const CUE_TINT = 'hsl(var(--twin-chat-accent) / 0.12)'
const RETRY_FRAMES = 30

export interface ScrollToChatMessageOptions {
  /** Loads more history when the message is not rendered. */
  onBackfill?: () => void | Promise<void>
}

function findMessageElement(messageId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-testid="message-${CSS.escape(messageId)}"]`)
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

function waitFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve())
    } else {
      setTimeout(resolve, 16)
    }
  })
}

function cueAndScroll(element: HTMLElement): void {
  const isReduced = prefersReducedMotion()
  element.scrollIntoView({
    block: 'center',
    behavior: isReduced ? 'auto' : 'smooth',
  })
  // jsdom and old engines have no Web Animations API; the scroll alone still lands.
  if (typeof element.animate !== 'function') {
    return
  }
  element.animate(
    [
      { backgroundColor: CUE_TINT, borderRadius: '1rem' },
      { backgroundColor: CUE_TINT, borderRadius: '1rem', offset: 0.35 },
      { backgroundColor: 'transparent', borderRadius: '1rem' },
    ],
    { duration: CUE_MS, easing: 'ease-out' },
  )
  if (!isReduced) {
    element.animate(
      [{ transform: 'translateY(6px)' }, { transform: 'translateY(0)' }],
      { duration: 420, easing: 'cubic-bezier(0.32, 0.72, 0, 1)', delay: 250 },
    )
  }
}

/** Resolves true when the message was found and scrolled to. */
export async function scrollToChatMessage(
  messageId: string,
  options: ScrollToChatMessageOptions = {},
): Promise<boolean> {
  const { onBackfill } = options
  let element = findMessageElement(messageId)
  if (!element && onBackfill) {
    await onBackfill()
    for (let frame = 0; frame < RETRY_FRAMES && !element; frame += 1) {
      await waitFrame()
      element = findMessageElement(messageId)
    }
  }
  if (!element) {
    return false
  }
  cueAndScroll(element)
  return true
}
